import { transparentize } from 'polished'
import styled, { css } from 'styled-components'
import { validFor } from '@/theme/validFor'
import { Tiny2 } from '@/components/atoms/typography'
import { Flex } from '@/components/atoms/Grid'

type RadioButtonProps<T> = {
  value: T
  onChanged?: (value: T) => void
  name: string
  isChecked: boolean
  title?: string
  isDisabled?: boolean
} & Rest

function RadioButton<T>({
  value,
  name,
  isChecked,
  onChanged,
  title,
  isDisabled = false,
  ...rest
}: RadioButtonProps<T>): JSX.Element {
  return (
    <Wrapper
      isDisabled={isDisabled}
      onClick={() => onChanged && onChanged(value)}
      alignItems="center"
      {...rest}
    >
      <RadioItem>
        <StyledInput
          type="radio"
          name={name}
          value={value}
          isChecked={!!isChecked}
          onChange={() => onChanged && onChanged(value)}
          isDisabled={isDisabled}
          data-testid="radioButton"
        />
        <RadioButtonLabel />
      </RadioItem>
      {title && (
        <Tiny2 my={0} ml="6px">
          {title}
        </Tiny2>
      )}
    </Wrapper>
  )
}

export default RadioButton

const RadioButtonLabel = styled.label`
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  border: 2px solid
    ${(props) => transparentize(0.5, props.theme.colors.onBackground)};
`

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 20px;
  height: 20px;
`

const HoverCss = css`
  border: 2px solid ${(props) => transparentize(0.5, props.theme.colors.accent)};
  &::after {
    content: '';
    display: block;
    border-radius: 50%;
    width: 10px;
    height: 10px;
    margin: 3px;
    background: ${(props) => transparentize(0.5, props.theme.colors.accent)};
  }
`

const StyledInput = styled.input.attrs((props) => ({
  checked: props.isChecked
}))`
  opacity: 0;
  z-index: 1;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  margin: 0;
  ${validFor.desktop` 
    ${({ isDisabled }) => !isDisabled && 'cursor: pointer; '}
  `}
  &:checked ~ ${RadioButtonLabel} {
    border: 2px solid ${(props) => props.theme.colors.accent};
    &::after {
      content: '';
      display: block;
      border-radius: 50%;
      width: 10px;
      height: 10px;
      margin: 3px;
      background: ${(props) => props.theme.colors.accent};
    }
  }
`

const EnabledWrapper = css`
  &:hover {
    cursor: pointer;
    ${validFor.desktop`
      ${RadioButtonLabel} {
        ${HoverCss}
      }
    }
  `}
  }
`

const Wrapper = styled(Flex)`
  ${({ isDisabled }) => !isDisabled && EnabledWrapper}
`
